import { createAction } from '@reduxjs/toolkit';
import * as itemTypes from '../../items/item-types';
import {
  getItem as getItemInternal,
  saveItem as saveItemInternal
} from '../../items/item.actions';
import { authorizedFetch, validateIPAddress } from '../../utils';
import { addError } from '../../toasts/toasts.actions';
import { uploadVirtualCurrencyIcon } from './traffic-partner-utils';
import {
  saveTrafficPartnerAdBlocking,
  saveSubPartnerAdBlocking as saveSubPartnerAdBlockingAPI
} from './ad-blocking/ad-blocking-utils';

export const receiveTrafficPartner = createAction('receiveTrafficPartner');
export const setTrafficPartnerIsBusy = createAction('setTrafficPartnerIsBusy');
export const modifyTrafficPartner = createAction('modifyTrafficPartner');
export const closeTrafficPartner = createAction('closeTrafficPartner');

export const TrafficPartnerModel = (id, name, params) => ({
  id,
  name,
  type: itemTypes.TRAFFIC_PARTNER,
  postbackConfigurations: [],
  virtualCurrency: {
    name: '',
    externalName: '',
    dollarExchangeRatio: 1,
    prefix: '$',
    icon: ''
  }
});

export const getItem = getItemInternal(
  TrafficPartnerModel,
  receiveTrafficPartner,
  setTrafficPartnerIsBusy,
  item => {
    if (Array.isArray(item.ipAllowRanges)) {
      item.ipAllowRanges = Object.entries(item.ipAllowRanges).map(
        ([key, value]) => {
          return { id: value.id, key: value.start, value: value.end };
        }
      );
    }
    if (!item.postbackItem) {
      Object.assign(item, {
        postbackItem: {}
      });
    }
  }
);

export const saveItem = (() => {
  let currencyIconDataUrl;
  let adBlockingToSave;
  let subPartnerAdBlockingToSave;

  return saveItemInternal(
    modifyTrafficPartner,
    item => {
      if (
        Array.isArray(item.postbackConfigurations) &&
        item.postbackConfigurations.length > 0
      ) {
        const postbacksWithMissingFieldsError = [];
        let emptyQueryParamError = false;
        let optimizelyConditionError = false;
        item.postbackConfigurations.forEach(postback => {
          if (
            !Array.isArray(postback.sources) ||
            postback.sources.length === 0 ||
            !Array.isArray(postback.eventTypes) ||
            postback.eventTypes.length === 0
          ) {
            postbacksWithMissingFieldsError.push(
              postback.name || 'Unnamed Postback'
            );
          }
          if (Array.isArray(postback.filters?.queryParams?.include)) {
            postback.filters.queryParams.include.forEach(condition => {
              if (
                !condition.key ||
                condition.key.trim() === '' ||
                (condition.comparisonStrategy !== 'Exists' &&
                  condition.values.length === 0)
              ) {
                emptyQueryParamError = true;
              }
            });
          }
          if (Array.isArray(postback.filters?.fieldConditions?.include)) {
            postback.filters.fieldConditions.include.forEach(condition => {
              if (
                condition.comparisonStrategy !== 'Exists' &&
                condition.values.length === 0
              ) {
                optimizelyConditionError = true;
              }
            });
          }
        });

        if (emptyQueryParamError) {
          return 'Query parameter conditions must have a field name and at least one value.';
        }
        if (optimizelyConditionError) {
          return 'Optimizely field conditions must have at least one value.';
        }
        if (postbacksWithMissingFieldsError.length > 0) {
          return `These postbacks need at least one source and one event type: ${postbacksWithMissingFieldsError.join(
            ', '
          )}`;
        }
      }

      if (item.partnerType === 'offersyndication' && !item.businessUnitId) {
        return 'Business Unit is required when Partner Type is Offer Syndication.';
      }
      if (Array.isArray(item.ipAllowRanges)) {
        // validate the IP Addresses
        const ipItems = item.ipAllowRanges.filter(
          f => f.key !== '' || f.value !== ''
        );
        const ipsValidation = validateIPs(ipItems);
        if (ipsValidation !== true) {
          return ipsValidation;
        }
      }

      if (
        !item.virtualCurrency.dollarExchangeRatio ||
        item.virtualCurrency.dollarExchangeRatio === 0
      ) {
        return 'Dollar exchange ratio must be greater than 0.';
      }

      if (
        Array.isArray(item.blockedSubPartners) &&
        item.blockedSubPartners.length > 0
      ) {
        // validate blocked sub partners by making sure both subpartner id is filled in
        for (let subPartner of item.blockedSubPartners) {
          if (subPartner.subPartnerId === '' && subPartner.subPartnerName) {
            return 'A blocked sub partner is missing an id.';
          }
        }

        // validate blocked sub partners by making sure there are no duplicates
        const ids = item.blockedSubPartners.map(sp => sp.subPartnerId);
        const uniqueIds = new Set(ids);
        if (ids.length !== uniqueIds.size) {
          return 'Duplicate blocked sub partner ids found. Blocked sub partner ids must be unique.';
        }
      }

      if (item.subPartnerAdBlocking) {
        const subPartnerBlocks =
          item.subPartnerAdBlocking.subPartnerBlocks || [];

        for (let block of subPartnerBlocks) {
          if (
            !block.subPartnerId ||
            block.subPartnerId.toString().trim() === ''
          ) {
            return 'A sub partner is missing an ID in ad blocking configuration.';
          }
        }

        const subPartnerIds = subPartnerBlocks.map(block => block.subPartnerId);
        const uniqueSubPartnerIds = new Set(subPartnerIds);
        if (subPartnerIds.length !== uniqueSubPartnerIds.size) {
          return 'Duplicate sub partner IDs found in ad blocking configuration. Sub partner IDs must be unique.';
        }
      }
    },
    itemCopy => {
      if (Array.isArray(itemCopy.ipAllowRanges)) {
        const ipItems = itemCopy.ipAllowRanges.filter(
          f => f.key !== '' || f.value !== ''
        );
        itemCopy.ipAllowRanges = Object.entries(ipItems).map(([key, value]) => {
          return {
            id: value.id,
            start: value.key,
            end: value.value
          };
        });
      }

      // strip new- from new postbacks
      if (itemCopy.postbackConfigurations.length > 0) {
        const newPostbackConfigurations = itemCopy.postbackConfigurations.map(
          postback => {
            if (postback.id.startsWith('new-')) {
              return {
                ...postback,
                id: postback.id.replace('new-', '')
              };
            }
            return postback;
          }
        );
        itemCopy.postbackConfigurations = newPostbackConfigurations;
      }

      currencyIconDataUrl = itemCopy.virtualCurrency.iconData;
      delete itemCopy.virtualCurrency.iconData;

      if (Array.isArray(itemCopy.blockedSubPartners)) {
        // remove any empty blocked sub partners
        if (itemCopy.blockedSubPartners.length > 0) {
          itemCopy.blockedSubPartners = itemCopy.blockedSubPartners.filter(
            sp => sp.subPartnerId !== ''
          );
        }
        // transform array to object with subPartnerId as key
        const blockedSubPartnersObj = {};
        itemCopy.blockedSubPartners.forEach(sp => {
          blockedSubPartnersObj[sp.subPartnerId] = {
            subPartnerId: sp.subPartnerId,
            subPartnerName: sp.subPartnerName
          };
        });
        itemCopy.blockedSubPartners = blockedSubPartnersObj;
      }

      // Capture and remove adBlocking - saved separately after main save to avoid version conflicts
      adBlockingToSave = itemCopy.adBlocking;
      delete itemCopy.adBlocking;

      // Capture and remove subPartnerAdBlocking - saved separately after main save to avoid version conflicts
      subPartnerAdBlockingToSave = itemCopy.subPartnerAdBlocking;
      delete itemCopy.subPartnerAdBlocking;
    },
    async (item, { id, version }, dispatch, modifyItemAction) => {
      const { virtualCurrency } = item;
      let expectedVersion = version;

      if (currencyIconDataUrl) {
        const thumbnailResponse = await uploadVirtualCurrencyIcon(
          currencyIconDataUrl,
          id,
          virtualCurrency,
          expectedVersion,
          dispatch,
          modifyItemAction
        );

        currencyIconDataUrl = null;
        expectedVersion = thumbnailResponse.version;
      }

      if (adBlockingToSave) {
        const adBlockingResponse = await saveTrafficPartnerAdBlocking(id, {
          ...adBlockingToSave,
          expectedVersion
        });
        adBlockingToSave = null;
        expectedVersion = adBlockingResponse?.version ?? expectedVersion;
        dispatch(
          modifyItemAction({
            id,
            newProperties: { id, version: expectedVersion, adBlocking: null }
          })
        );
      }

      if (subPartnerAdBlockingToSave) {
        const subPartnerResponse = await saveSubPartnerAdBlockingAPI(id, {
          ...subPartnerAdBlockingToSave,
          expectedVersion
        });
        subPartnerAdBlockingToSave = null;
        dispatch(
          modifyItemAction({
            id,
            newProperties: {
              id,
              version: subPartnerResponse?.version ?? expectedVersion,
              subPartnerAdBlocking: null
            }
          })
        );
      }
    }
  );
})();

export const sendPostbackTest = (
  id,
  postbackRequest,
  setResponse,
  setIsLoading
) => async dispatch => {
  setIsLoading(true);
  try {
    const result = await authorizedFetch(
      `/api/trafficpartner/${id}/postback/test`,
      'POST',
      postbackRequest
    );

    setResponse(result);
  } catch (e) {
    dispatch(addError('Postback test failed', e.Message));
  }

  setIsLoading(false);
};

const validateIPs = ipAllowRanges => {
  if (Array.isArray(ipAllowRanges)) {
    for (let ip of ipAllowRanges) {
      const ipKey = ip.key;
      const ipValue = ip.value;

      // If key is defined but value is not
      if (ipKey !== '' && ipValue === '') {
        return 'Please add an IP address for the end range. Put same value as start range if only 1 IP exists.';
      }
      // If value is defined but key is not
      if (ipValue !== '' && ipKey === '') {
        return 'Please add an IP address for the start range.';
      }
      // Validate IP addresses
      if (!validateIPAddress(ipKey) || !validateIPAddress(ipValue)) {
        return 'Please enter a valid IP address';
      }
    }
    return true;
  }
};
